<script setup>
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from "vue";
import { useCategoryStore } from "@/stores/useCategoryStore";
import CustomTextarea from "../Generics/CustomTextarea.vue";
// Importação de CustomTextarea não está sendo usada, mas mantida por segurança se o input for trocado futuramente.
// import CustomTextarea from "../Generics/CustomTextarea.vue";

import { toTitleCase } from "@/utils/stringUtils";

import { useDraggableModal } from "@/composables/useDraggableModal";

const emit = defineEmits(["close", "category-added"]);

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

const props = defineProps({
  initialName: {
    type: String,
    default: "",
  },
});

const categoryStore = useCategoryStore();

const obj = ref({
  categoryId: 0,
  categoryName: "",
  description: "",
});

const nameInput = ref(null);
const isSubmitting = ref(false);

// ====================================================================
// ✅ TOAST PADRÃO ADDUSERGROUPFORM.VUE (3000ms) 🚀
// (Copiado de AddActivityForm.vue para consistência)
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

// Ao criar com sucesso, esconde só o painel do modal; o toast (num Teleport
// próprio) segue visível sozinho até o emit("close") desmontar o componente.
const hideBackdropForSuccess = ref(false);

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null; // Limpa a referência
  }, 3000); // 3 segundos, padrão AddUserGroupForm
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}
// ====================================================================

watch(
  () => props.initialName,
  (newName) => {
    obj.value.categoryName = newName || "";
  },
  { immediate: true }
);

async function cadastrar() {
  // evita duplo clique
  if (isSubmitting.value) return;

  try {
    isSubmitting.value = true;

    // ✅ PATCH: normaliza o nome ANTES do payload
    obj.value.categoryName = toTitleCase(
      (obj.value.categoryName ?? "").toString().trim().replace(/\s+/g, " ")
    );

    const created = await categoryStore.createCategory(obj.value);

    // ✅ TOAST DE SUCESSO (usa a função padronizada de 3s)
    showSuccessToast("Gênero criado com sucesso.");
    hideBackdropForSuccess.value = true;

    // avisa o pai imediatamente para já selecionar a opção
    emit("category-added", {
      label: created.categoryName,
      value: created.categoryId,
    });

    // ✅ fecha modal depois de 2.4s (padrão AddActivityForm.vue)
    setTimeout(() => {
      isSubmitting.value = false; // Adicionado para consistência, embora o modal vá fechar.
      emit("close");
    }, 2400); // Alterado de 4000ms para 2400ms para consistência.
  } catch (error) {
    let userFriendlyMessage =
      "Ocorreu um erro desconhecido ao cadastrar o gênero.";

    if (error.response && error.response.data && error.response.data.errors) {
      const validationErrors = error.response.data.errors;
      const messages = [];
      for (const field in validationErrors) {
        if (validationErrors[field] && validationErrors[field].length > 0) {
          messages.push(validationErrors[field][0]);
        }
      }
      if (messages.length > 0) {
        userFriendlyMessage = messages.join("\n");
      }
    } else if (error.response?.data?.message) {
      userFriendlyMessage = error.response.data.message;
    } else if (error.message) {
      userFriendlyMessage = error.message;
    }

    console.error("Erro detalhado da API:", error.response?.data || error);

    // ✅ TOAST DE ERRO (usa a função padronizada de 3s)
    showErrorToast(userFriendlyMessage);
  } finally {
    // Apenas define isSubmitting para false no caso de erro, pois em sucesso é feito no setTimeout
    if (toastType.value === "error") {
      isSubmitting.value = false;
    }
  }
}

function closeModal() {
  // Adicionado isSubmitting check
  if (!isSubmitting.value) {
    emit("close");
  }
}

function handleEscKey(event) {
  if (event.key === "Escape") {
    closeModal();
  }
}

onMounted(async () => {
  nextTick(() => {
    if (nameInput.value) nameInput.value.focus();
  });
  document.addEventListener("keydown", handleEscKey);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);

  if (toastTimeoutId.value) {
    clearTimeout(toastTimeoutId.value);
  }
});
</script>

<template>
  <div
    v-show="!hideBackdropForSuccess"
    class="modal-backdrop"
    @click.self="!isSubmitting"
  >
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="modal-title">Adicionar Gênero</h3>
        <button
          @click="closeModal"
          type="button"
          class="modal-close-btn"
          :disabled="isSubmitting"
        >
          <svg
            class="w-5 h-5"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </button>
      </div>

      <form @submit.prevent="cadastrar" novalidate>
        <div class="modal-body">
          <div class="form-content">
            <div class="form-group">
              <label for="name" class="form-label">Nome</label>
              <input
                ref="nameInput"
                type="text"
                id="name"
                v-model="obj.categoryName"
                @blur="obj.categoryName = toTitleCase(obj.categoryName)"
                class="input-base"
                placeholder="Digite o nome do gênero"
                required
                @focus="$event.target.select()"
              />
            </div>
            <div class="form-group">
              <label for="description" class="form-label"
                >Descrição (opcional)</label
              >
              <CustomTextarea
                v-model:body="obj.description"
                placeholder="Digite uma descrição (opcional)"
                :is-disabled="false"
                style="min-height: 120px"
              />
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            @click="closeModal"
            class="btn btn-secondary"
            :disabled="isSubmitting"
          >
            Cancelar
          </button>
          <button
            type="submit"
            class="btn btn-success"
            :disabled="isSubmitting"
          >
            <svg
              v-if="isSubmitting"
              class="w-5 h-5 mr-2 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>

            <svg
              v-else
              class="w-5 h-5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                clip-rule="evenodd"
              ></path>
            </svg>

            <span>
              {{
                isSubmitting ? "Adicionando..." : "Adicionar Gênero"
              }}
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.modal-panel {
  max-width: 32rem;
}

/* Mantém sua estrutura existente (form-content) só compactando o gap */
.form-content {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

textarea.input-base {
  min-height: 120px;
}

</style>
